import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@cocoonspace/ui/components/alert-dialog'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@cocoonspace/ui/components/collapsible'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@cocoonspace/ui/components/tooltip'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { ChevronDownIcon, PencilIcon } from 'lucide-react'

import {
	SectionTitle,
	SectionTitleBtn,
	SectionWrapper,
} from '~/components/sheet-section-wrapper'
import { WorkingHoursList } from '~/domains/scheduling/availabilities/edit/working-hours-list'
import { useFormSectionCollapsible } from './use-form-section-collapsible.hook'
import type { EditEventTypeFormData } from './event-type-form'

interface TimeSlotWorkingHoursOutput {
	startTime: string
	endTime: string
}

interface WorkingHoursOutput {
	mon: TimeSlotWorkingHoursOutput[]
	tue: TimeSlotWorkingHoursOutput[]
	wed: TimeSlotWorkingHoursOutput[]
	thu: TimeSlotWorkingHoursOutput[]
	fri: TimeSlotWorkingHoursOutput[]
	sat: TimeSlotWorkingHoursOutput[]
	sun: TimeSlotWorkingHoursOutput[]
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

const DAYS_LOCALIZED = {
	fr: {
		mon: 'L',
		tue: 'M',
		wed: 'M',
		thu: 'J',
		fri: 'V',
		sat: 'S',
		sun: 'D',
	},
	en: {
		mon: 'M',
		tue: 'T',
		wed: 'W',
		thu: 'T',
		fri: 'F',
		sat: 'S',
		sun: 'S',
	},
}

const EditAvailabilityDialog = ({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) => {
	const { t } = useLingui()

	return (
		<AlertDialog
			open={open}
			onOpenChange={onOpenChange}
		>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t`Editer les disponibilités`}</AlertDialogTitle>
				</AlertDialogHeader>

				<WorkingHoursList onSubmit={() => onOpenChange(false)} />
			</AlertDialogContent>
		</AlertDialog>
	)
}

const WorkingHoursPreview = ({
	isOpen,
	setIsOpen,
}: {
	isOpen: boolean
	setIsOpen: (open: boolean) => void
}) => {
	const { i18n } = useLingui()

	const form = useFormContext<EditEventTypeFormData>()
	const availabilities = form.getValues('availabilities') as
		| WorkingHoursOutput
		| undefined

	return (
		<div className='grid gap-y-4'>
			<div className='grid grid-cols-7'>
				{DAYS.map((day) => {
					const isDisabled = !availabilities?.[day]?.length

					return (
						<Tooltip
							key={day}
							delayDuration={0}
						>
							<TooltipTrigger
								disabled={isDisabled}
								type='button'
								className={cn(
									'bg-muted/10 text-muted-foreground size-8 rounded-full font-semibold',
									{ 'bg-primary text-primary-foreground': !isDisabled },
								)}
							>
								{DAYS_LOCALIZED[i18n.locale as 'fr'][day]}
							</TooltipTrigger>

							{!isDisabled && (
								<TooltipContent>
									{availabilities[day]?.map((availability) => (
										<div key={availability.startTime}>
											{availability.startTime} - {availability.endTime}
										</div>
									))}
								</TooltipContent>
							)}
						</Tooltip>
					)
				})}
			</div>

			<EditAvailabilityDialog
				open={isOpen}
				onOpenChange={setIsOpen}
			/>
		</div>
	)
}

export const AvailTab = () => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)
	const { onOpenChange, open } = useFormSectionCollapsible(
		['availabilities'],
		true,
	)

	return (
		<Collapsible
			open={open}
			onOpenChange={onOpenChange}
		>
			<SectionWrapper>
				<SectionTitle>
					<CollapsibleTrigger className='flex min-w-0 flex-1 items-center gap-2 text-left'>
						{t`Disponibilité`}
					</CollapsibleTrigger>

					<SectionTitleBtn onClick={() => setIsOpen(true)}>
						<PencilIcon />
						{t`Modifier`}
					</SectionTitleBtn>

					<CollapsibleTrigger
						aria-label={t`Disponibilité`}
						className='flex size-4 shrink-0 items-center [&[data-state=open]>svg]:rotate-180'
					>
						<ChevronDownIcon className='size-4 transition-transform' />
					</CollapsibleTrigger>
				</SectionTitle>

				<CollapsibleContent>
					<WorkingHoursPreview
						isOpen={isOpen}
						setIsOpen={setIsOpen}
					/>
				</CollapsibleContent>
			</SectionWrapper>
		</Collapsible>
	)
}
